import React, { useContext, useMemo, useState } from 'react';
import AppContext from '../AppContext';
import '../css/service-spotlight.css';
import {
  buildCiiXml,
  buildFacturXPdfBytes,
  buildInvoicePdfBytes,
  buildUblXml,
} from '../utils/invoiceExports';

const copy = {
  fr: {
    eyebrow: 'Creer ma facture',
    title: 'Facturation electronique PDP API',
    summary: 'Generez votre facture, structurez-la, puis preparez son envoi via une PDP compatible.',
    highlights: ['Factur-X', 'CII / UBL', 'PDP API'],
    fields: {
      company: 'Societe',
      address: 'Adresse emetteur',
      siret: 'SIRET',
      vatNumber: 'TVA intracom.',
      client: 'Client',
      clientAddress: 'Adresse client',
      clientSiren: 'SIREN client',
      deliveryAddress: 'Adresse de livraison',
      operationCategory: 'Categorie operation',
      debitVatOption: 'TVA sur les debits',
      issueDate: 'Date facture',
      serviceDate: 'Date prestation',
      dueDate: 'Echeance',
      payment: 'Paiement',
      iban: 'IBAN',
      description: 'Description',
      quantityValue: 'Quantite',
      unitValue: 'Unite',
      amount: 'Montant HT',
      addLine: 'Ajouter une ligne',
      settings: 'Reglages',
      accent: 'Couleur des cases',
      logo: 'Logo',
      chooseLogo: 'Choisir un logo',
      showLegal: 'Afficher mentions',
      vat: 'Activer la TVA',
      vatRate: 'Taux TVA',
      qty: 'Activer la colonne quantite',
      unit: 'Activer la colonne unite',
      currency: 'Devise',
    },
    actions: {
      reset: 'Reinitialiser',
      pdf: 'Valider en PDF',
      facturx: 'Preparer Factur-X',
      cii: 'XML CII',
      ubl: 'XML UBL',
      send: 'Transmettre a la PDP',
    },
    labels: {
      invoice: 'Facture',
      from: 'Emetteur',
      to: 'Client',
      line: 'Ligne',
      unitPrice: 'Montant HT',
      subtotal: 'Sous-total HT',
      vat: 'TVA 20%',
      total: 'Total TTC',
      payment: 'Paiement',
      dueDate: 'Echeance',
      serviceDate: 'Date de prestation',
      invoiceDate: 'Date d emission',
      legal: 'Mentions de reglement',
      penalties: 'Penalites de retard',
      indemnity: 'Indemnite forfaitaire',
      format: 'Exports',
      officialNotice:
        'Le PDF est lisible et propre. Le Factur-X ajoute un XML embarque pour un usage plus formel de facturation electronique.',
      apiTitle: 'Flux PDP API',
      apiSummary:
        'Le bon flux n est pas un simple PDF e-mail : generation de la facture, format structure, transmission via PDP, puis suivi des statuts.',
      apiSteps: [
        'Generer un format structure Factur-X, CII ou UBL.',
        'Transmettre via une PDP ou une solution compatible reliee a une PDP.',
        'Suivre les retours de statut et l e-reporting.',
      ],
      pipeline: ['Creer', 'Valider', 'Transmettre', 'Suivre'],
      statusTitle: 'Statut de preparation',
      statusValue: 'Pret pour integration PDP API',
      complianceTitle: 'Facturation electronique 2026',
      complianceWarning:
        'Une simple facture PDF envoyee par e-mail n est pas une facture electronique reglementaire. Elle doit etre structuree et transmise via une PDP.',
    },
    paymentMethods: {
      transfer: 'Virement bancaire',
      stripe: 'Lien Stripe',
      cash: 'Paiement comptant',
    },
    legalTexts: {
      standard: 'TVA applicable selon le taux en vigueur.',
      penalties: 'Penalites exigibles en cas de retard de paiement au taux legal en vigueur.',
      indemnity: 'Indemnite forfaitaire de 40 EUR pour frais de recouvrement.',
    },
    compliance: {
      stepsTitle: 'Parcours recommande',
      steps: [
        'Choisir une plateforme de dematerialisation partenaire compatible avec votre outil de gestion.',
        'Renseigner toutes les mentions obligatoires de la facture.',
        'Transmettre la facture via la plateforme du client sur un reseau interoperable.',
        'Suivre les statuts de vie de la facture jusqu a l encaissement.',
      ],
      mentionsTitle: 'Mentions a verifier',
      mentions: [
        'Identite de l emetteur et du destinataire.',
        'Date d emission et numero unique de facture.',
        'Designation des biens ou services, quantite, prix HT/TTC et TVA.',
        'Conditions de paiement.',
        'Depuis 2026: SIREN du client, adresse de livraison si differente, categorie d operation et mention TVA sur les debits si applicable.',
      ],
      statusesTitle: 'Statuts de suivi',
      statuses: ['Deposee', 'Rejetee', 'Refusee', 'Encaissee'],
      toolsTitle: 'Exemples de solutions',
      tools: [
        'Indy pour la production et la gestion courante.',
        'Shine pour le suivi de cycle de vie.',
        'Credit Mutuel via une solution compatible entreprise.',
      ],
    },
  },
  en: {
    eyebrow: 'Create my invoice',
    title: 'PDP API e-invoicing',
    summary: 'Generate the invoice, structure it, then prepare delivery through a compatible PDP.',
    highlights: ['Factur-X', 'CII / UBL', 'PDP API'],
    fields: {
      company: 'Company',
      address: 'Issuer address',
      siret: 'SIRET',
      vatNumber: 'VAT number',
      client: 'Client',
      clientAddress: 'Client address',
      clientSiren: 'Client SIREN',
      deliveryAddress: 'Delivery address',
      operationCategory: 'Operation category',
      debitVatOption: 'VAT on debit',
      issueDate: 'Invoice date',
      serviceDate: 'Service date',
      dueDate: 'Due date',
      payment: 'Payment',
      iban: 'IBAN',
      description: 'Description',
      quantityValue: 'Quantity',
      unitValue: 'Unit',
      amount: 'Amount excl. VAT',
      addLine: 'Add line',
      settings: 'Settings',
      accent: 'Box color',
      logo: 'Logo',
      chooseLogo: 'Choose logo',
      showLegal: 'Show legal notes',
      vat: 'Enable VAT',
      vatRate: 'VAT rate',
      qty: 'Enable quantity column',
      unit: 'Enable unit column',
      currency: 'Currency',
    },
    actions: {
      reset: 'Reset',
      pdf: 'Validate PDF',
      facturx: 'Prepare Factur-X',
      cii: 'CII XML',
      ubl: 'UBL XML',
      send: 'Send to PDP',
    },
    labels: {
      invoice: 'Invoice',
      from: 'Issuer',
      to: 'Client',
      line: 'Line',
      unitPrice: 'Amount excl. VAT',
      subtotal: 'Subtotal',
      vat: 'VAT 20%',
      total: 'Total due',
      payment: 'Payment',
      dueDate: 'Due date',
      serviceDate: 'Service date',
      invoiceDate: 'Issue date',
      legal: 'Payment notices',
      penalties: 'Late-payment penalties',
      indemnity: 'Fixed recovery fee',
      format: 'Exports',
      officialNotice:
        'The PDF is clean and readable. Factur-X adds embedded XML for a more formal e-invoicing use case.',
      apiTitle: 'PDP API flow',
      apiSummary:
        'The correct flow is not a plain emailed PDF: generate the invoice, structure it, route it through a PDP, then track statuses.',
      apiSteps: [
        'Generate a structured Factur-X, CII or UBL format.',
        'Route it through a PDP or compatible software connected to a PDP.',
        'Track delivery statuses and reporting events.',
      ],
      pipeline: ['Create', 'Validate', 'Transmit', 'Track'],
      statusTitle: 'Preparation status',
      statusValue: 'Ready for PDP API integration',
      complianceTitle: '2026 e-invoicing',
      complianceWarning:
        'A PDF invoice sent by email alone is not a compliant electronic invoice. It must be structured and routed through a partner platform.',
    },
    paymentMethods: {
      transfer: 'Bank transfer',
      stripe: 'Stripe link',
      cash: 'Cash payment',
    },
    legalTexts: {
      standard: 'VAT applies at the current rate.',
      penalties: 'Late-payment penalties apply at the legal rate in force.',
      indemnity: 'Fixed EUR 40 recovery fee.',
    },
    compliance: {
      stepsTitle: 'Recommended flow',
      steps: [
        'Choose a partner platform that works with your accounting stack.',
        'Fill in all mandatory invoice fields.',
        'Route the invoice through the client platform on an interoperable network.',
        'Track invoice lifecycle statuses until collection.',
      ],
      mentionsTitle: 'Fields to check',
      mentions: [
        'Issuer and buyer identity.',
        'Issue date and unique invoice number.',
        'Goods or services, quantity, prices excl./incl. VAT and VAT.',
        'Payment terms.',
        'From 2026: client SIREN, delivery address if different, operation category, VAT-on-debit mention if applicable.',
      ],
      statusesTitle: 'Statuses',
      statuses: ['Submitted', 'Rejected', 'Refused', 'Collected'],
      toolsTitle: 'Example solutions',
      tools: [
        'Indy for routine invoicing workflows.',
        'Shine for invoice lifecycle follow-up.',
        'Credit Mutuel through a compatible business setup.',
      ],
    },
  },
};

const getInitialState = () => ({
  company: 'JR-AI-Consulting',
  address: 'Paris, France',
  siret: '123 456 789 00012',
  vatNumber: 'FR00123456789',
  client: 'Client PME',
  clientAddress: 'Adresse du client',
  clientSiren: '',
  deliveryAddress: '',
  operationCategory: 'PS',
  debitVatOption: false,
  issueDate: new Date().toISOString().slice(0, 10),
  serviceDate: new Date().toISOString().slice(0, 10),
  dueDate: new Date(Date.now() + 1000 * 60 * 60 * 24 * 30).toISOString().slice(0, 10),
  payment: 'transfer',
  iban: 'FR76 XXXX XXXX XXXX XXXX XXXX XXX',
  lines: [{ id: 1, description: 'Accompagnement facture electronique', quantity: '1', unit: 'service', amount: '490' }],
  accent: '#0f766e',
  logo: '',
  showLegal: true,
  vatEnabled: true,
  vatRate: '20',
  showQty: false,
  showUnit: false,
  currency: 'EUR',
  logoName: '',
});

function ServiceSpotlight() {
  const { language } = useContext(AppContext);
  const t = copy[language] || copy.en;
  const [form, setForm] = useState(getInitialState);
  const [isGeneratingFacturX, setIsGeneratingFacturX] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const amount = form.lines.reduce(
    (sum, line) => sum + (Number(line.quantity) || 1) * (Number(line.amount) || 0),
    0
  );
  const vatRate = Number(form.vatRate) || 0;
  const vatAmount = form.vatEnabled ? amount * (vatRate / 100) : 0;
  const total = amount + vatAmount;

  const invoiceNumber = useMemo(() => {
    const datePart = form.issueDate ? form.issueDate.replaceAll('-', '') : '20260311';
    return `FAC-${datePart}-01`;
  }, [form.issueDate]);

  const onChange = ({ target }) => {
    const { name, value, type, checked } = target;
    setForm((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
  };

  const onLogoChange = ({ target }) => {
    const file = target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setForm((current) => ({
        ...current,
        logo: String(reader.result || ''),
        logoName: file.name,
      }));
    };
    reader.readAsDataURL(file);
  };

  const updateLine = (id, field, value) => {
    setForm((current) => ({
      ...current,
      lines: current.lines.map((line) => (line.id === id ? { ...line, [field]: value } : line)),
    }));
  };

  const addLine = () => {
    setForm((current) => ({
      ...current,
      lines: [...current.lines, { id: Date.now(), description: '', quantity: '1', unit: 'service', amount: '0' }],
    }));
  };

  const removeLine = (id) => {
    setForm((current) => ({
      ...current,
      lines: current.lines.length === 1 ? current.lines : current.lines.filter((line) => line.id !== id),
    }));
  };

  const downloadFile = (content, fileName, type) => {
    const blob = content instanceof Blob ? content : new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const firstLine = form.lines[0] || { description: '', amount: '0' };
  const lineColumns = [
    'minmax(0, 1fr)',
    form.showQty ? '110px' : null,
    form.showUnit ? '140px' : null,
    '160px',
    '160px',
  ]
    .filter(Boolean)
    .join(' ');

  const payload = {
    number: invoiceNumber,
    company: form.company,
    address: form.address,
    siret: form.siret,
    vatNumber: form.vatNumber,
    client: form.client,
    clientAddress: form.clientAddress,
    clientSiren: form.clientSiren,
    deliveryAddress: form.deliveryAddress,
    operationCategory: form.operationCategory,
    debitVatOption: form.debitVatOption,
    service: firstLine.description || t.labels.line,
    quantity: Number(firstLine.quantity) || 1,
    unitAmount: Number(firstLine.amount) || 0,
    amount,
    vatAmount,
    total,
    currency: form.currency,
    vatRate,
    issueDate: form.issueDate,
    serviceDate: form.serviceDate,
    dueDate: form.dueDate,
    paymentLabel: t.paymentMethods[form.payment],
    iban: form.iban,
    notes: form.lines
      .map((line) => `${line.description}: ${line.quantity || 1} ${line.unit || 'service'} x ${line.amount} ${form.currency}`)
      .join(' | '),
    vatEnabled: form.vatEnabled,
    vatLegal: t.legalTexts.standard,
    penalties: t.legalTexts.penalties,
    indemnity: t.legalTexts.indemnity,
    accent: form.accent,
    logo: form.logo,
    lines: form.lines.map((line) => ({
      description: line.description || t.labels.line,
      quantity: Number(line.quantity) || 1,
      unit: line.unit || 'service',
      unitAmount: Number(line.amount) || 0,
      lineAmount: (Number(line.quantity) || 1) * (Number(line.amount) || 0),
    })),
  };

  const exportViaApi = async (url, fileName, setLoading, fallbackBuilder) => {
    setLoading(true);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      downloadFile(blob, fileName, 'application/pdf');
    } catch (error) {
      console.error(`${url} export error:`, error);

      if (fallbackBuilder) {
        try {
          const bytes = await fallbackBuilder(payload);
          downloadFile(new Blob([bytes], { type: 'application/pdf' }), fileName, 'application/pdf');
          return;
        } catch (fallbackError) {
          console.error(`${url} fallback export error:`, fallbackError);
        }
      }

      window.alert('Export indisponible sur cet environnement.');
    } finally {
      setLoading(false);
    }
  };

  const exportXml = () => {
    const xml = buildCiiXml(payload);
    downloadFile(xml, `${invoiceNumber}.xml`, 'application/xml;charset=utf-8');
  };

  const exportUbl = () => {
    const xml = buildUblXml(payload);
    downloadFile(xml, `${invoiceNumber}-ubl.xml`, 'application/xml;charset=utf-8');
  };

  return (
    <section className="invoice-simple">
      <div className="invoice-simple__hero">
        <div className="invoice-simple__hero-inner">
          <div className="invoice-simple__eyebrow">{t.eyebrow}</div>
          <h1 className="invoice-simple__title">{t.title}</h1>
          <p className="invoice-simple__summary">{t.summary}</p>
          <div className="invoice-simple__pipeline">
            {t.labels.pipeline.map((item, index) => (
              <div key={item} className="invoice-simple__pipeline-step">
                <span>{index + 1}</span>
                <strong>{item}</strong>
              </div>
            ))}
          </div>
          <div className="invoice-simple__highlights">
            {t.highlights.map((item) => (
              <span key={item} className="invoice-simple__highlight">
                {item}
              </span>
            ))}
          </div>
        </div>
        <div className="invoice-simple__actions">
          <button type="button" className="invoice-simple__button secondary" onClick={() => setForm(getInitialState())}>
            {t.actions.reset}
          </button>
        </div>
      </div>

      <div className="invoice-simple__layout">
        <div className="invoice-simple__main">
          <section className="invoice-simple__form-card">
            <div className="invoice-simple__form-grid">
            <label>
              <span>{t.fields.company}</span>
              <input name="company" value={form.company} onChange={onChange} />
            </label>
            <label>
              <span>{t.fields.address}</span>
              <input name="address" value={form.address} onChange={onChange} />
            </label>
            <label>
              <span>{t.fields.siret}</span>
              <input name="siret" value={form.siret} onChange={onChange} />
            </label>
            <label>
              <span>{t.fields.vatNumber}</span>
              <input name="vatNumber" value={form.vatNumber} onChange={onChange} />
            </label>
            <label>
              <span>{t.fields.client}</span>
              <input name="client" value={form.client} onChange={onChange} />
            </label>
            <label>
              <span>{t.fields.clientAddress}</span>
              <input name="clientAddress" value={form.clientAddress} onChange={onChange} />
            </label>
            <label>
              <span>{t.fields.clientSiren}</span>
              <input name="clientSiren" value={form.clientSiren} onChange={onChange} />
            </label>
            <label>
              <span>{t.fields.operationCategory}</span>
              <select name="operationCategory" value={form.operationCategory} onChange={onChange}>
                <option value="LB">LB</option>
                <option value="PS">PS</option>
                <option value="LBPS">LBPS</option>
              </select>
            </label>
            <label>
              <span>{t.fields.issueDate}</span>
              <input name="issueDate" type="date" value={form.issueDate} onChange={onChange} />
            </label>
            <label>
              <span>{t.fields.serviceDate}</span>
              <input name="serviceDate" type="date" value={form.serviceDate} onChange={onChange} />
            </label>
            <label>
              <span>{t.fields.dueDate}</span>
              <input name="dueDate" type="date" value={form.dueDate} onChange={onChange} />
            </label>
            <label>
              <span>{t.fields.payment}</span>
              <select name="payment" value={form.payment} onChange={onChange}>
                {Object.entries(t.paymentMethods).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="invoice-simple__full">
              <span>{t.fields.deliveryAddress}</span>
              <input name="deliveryAddress" value={form.deliveryAddress} onChange={onChange} />
            </label>
            <label className="invoice-simple__full invoice-simple__checkbox-row">
              <span>{t.fields.debitVatOption}</span>
              <input name="debitVatOption" type="checkbox" checked={form.debitVatOption} onChange={onChange} />
            </label>
            <label className="invoice-simple__full">
              <span>{t.fields.iban}</span>
              <input name="iban" value={form.iban} onChange={onChange} />
            </label>
          </div>

            <div className="invoice-simple__lines">
              <div className="invoice-simple__lines-head">
                <h3>{t.fields.description}</h3>
                <button type="button" className="invoice-simple__button secondary" onClick={addLine}>
                  {t.fields.addLine}
                </button>
              </div>
              {form.lines.map((line) => (
                <div
                  key={line.id}
                  className="invoice-simple__line-editor"
                  style={{
                    gridTemplateColumns: [
                      'minmax(0, 1fr)',
                      form.showQty ? '120px' : null,
                      form.showUnit ? '140px' : null,
                      '160px',
                      '44px',
                    ]
                      .filter(Boolean)
                      .join(' '),
                  }}
                >
                  <input
                    value={line.description}
                    onChange={(event) => updateLine(line.id, 'description', event.target.value)}
                    placeholder={t.fields.description}
                  />
                  {form.showQty ? (
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={line.quantity || '1'}
                      onChange={(event) => updateLine(line.id, 'quantity', event.target.value)}
                      placeholder={t.fields.quantityValue}
                    />
                  ) : null}
                  {form.showUnit ? (
                    <input
                      value={line.unit || ''}
                      onChange={(event) => updateLine(line.id, 'unit', event.target.value)}
                      placeholder={t.fields.unitValue}
                    />
                  ) : null}
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={line.amount}
                    onChange={(event) => updateLine(line.id, 'amount', event.target.value)}
                    placeholder={t.fields.amount}
                  />
                  <button type="button" className="invoice-simple__remove" onClick={() => removeLine(line.id)}>
                    ×
                  </button>
                </div>
              ))}
            </div>
          </section>

          <section className="invoice-simple__preview">
            <div className="invoice-simple__doc" style={{ '--invoice-accent': form.accent }}>
              <div className="invoice-simple__doc-head">
                <div>
                  <div className="invoice-simple__doc-kicker">{t.labels.invoice}</div>
                  <h2>{invoiceNumber}</h2>
                </div>
                <div className="invoice-simple__issuer">
                  {form.logo ? <img src={form.logo} alt="Logo" className="invoice-simple__logo" /> : null}
                  <strong>{form.company}</strong>
                  <span>{form.address}</span>
                  <span>SIRET: {form.siret}</span>
                  <span>{form.vatNumber}</span>
                </div>
              </div>

              <div className="invoice-simple__meta">
                <div>
                  <span>{t.labels.from}</span>
                  <strong>{form.company}</strong>
                  <span>{form.address}</span>
                </div>
                <div>
                  <span>{t.labels.to}</span>
                  <strong>{form.client}</strong>
                  <span>{form.clientAddress}</span>
                  {form.clientSiren ? <span>SIREN: {form.clientSiren}</span> : null}
                  {form.deliveryAddress ? <span>Livraison: {form.deliveryAddress}</span> : null}
                </div>
                <div>
                  <span>{t.labels.dueDate}</span>
                  <strong>{form.dueDate}</strong>
                  <span>{t.labels.invoiceDate}: {form.issueDate}</span>
                  <span>{t.labels.serviceDate}: {form.serviceDate}</span>
                  <span>Categorie: {form.operationCategory}</span>
                </div>
              </div>

            <div className="invoice-simple__line">
              <div className="invoice-simple__line-head" style={{ gridTemplateColumns: lineColumns }}>
                <span>{t.labels.line}</span>
                {form.showQty ? <span>Qté</span> : null}
                {form.showUnit ? <span>Unité</span> : null}
                <span>{t.labels.unitPrice}</span>
                <span>{t.labels.subtotal}</span>
              </div>
              {form.lines.map((line) => (
                <div key={line.id} className="invoice-simple__line-body" style={{ gridTemplateColumns: lineColumns }}>
                  <span>{line.description || '-'}</span>
                  {form.showQty ? <span>{line.quantity || '1'}</span> : null}
                  {form.showUnit ? <span>{line.unit || 'service'}</span> : null}
                  <span>{Number(line.amount || 0).toFixed(2)} {form.currency}</span>
                  <span>{((Number(line.quantity) || 1) * (Number(line.amount) || 0)).toFixed(2)} {form.currency}</span>
                </div>
              ))}
            </div>

            <div className="invoice-simple__totals">
              <div>
                <span>{t.labels.subtotal}</span>
                <strong>{amount.toFixed(2)} {form.currency}</strong>
              </div>
              <div>
                <span>{form.vatEnabled ? `TVA ${vatRate.toFixed(1).replace('.0', '')}%` : 'TVA 0%'}</span>
                <strong>{vatAmount.toFixed(2)} {form.currency}</strong>
              </div>
              <div className="invoice-simple__total">
                <span>{t.labels.total}</span>
                <strong>{total.toFixed(2)} {form.currency}</strong>
              </div>
            </div>

            <div className="invoice-simple__footer">
              <div>
                <span>{t.labels.payment}</span>
                <strong>{t.paymentMethods[form.payment]}</strong>
                <span>{form.iban}</span>
              </div>
              <div>
                <span>{t.labels.format}</span>
                <strong>PDF / Factur-X / CII / UBL</strong>
              </div>
            </div>

              {form.showLegal ? (
                <>
                  <div className="invoice-simple__legal">
                    <div>
                      <span>{t.labels.legal}</span>
                      <strong>{t.legalTexts.standard}</strong>
                      {form.debitVatOption ? <span>Option pour le paiement de la TVA d apres les debits.</span> : null}
                    </div>
                    <div>
                      <span>{t.labels.penalties}</span>
                      <strong>{t.legalTexts.penalties}</strong>
                    </div>
                    <div>
                      <span>{t.labels.indemnity}</span>
                      <strong>{t.legalTexts.indemnity}</strong>
                    </div>
                  </div>

                  <div className="invoice-simple__official-notice">{t.labels.officialNotice}</div>
                </>
              ) : null}
            </div>
          </section>

          <section className="invoice-simple__api-card">
            <div className="invoice-simple__api-kicker">{t.labels.apiTitle}</div>
            <p>{t.labels.apiSummary}</p>
            <div className="invoice-simple__api-steps">
              {t.labels.apiSteps.map((item) => (
                <div key={item} className="invoice-simple__api-step">
                  {item}
                </div>
              ))}
            </div>
            <div className="invoice-simple__api-status">
              <span>{t.labels.statusTitle}</span>
              <strong>{t.labels.statusValue}</strong>
            </div>
          </section>

          <section className="invoice-simple__compliance-card">
            <h3>{t.labels.complianceTitle}</h3>
            <div className="invoice-simple__compliance-grid">
              <div>
                <h4>{t.compliance.stepsTitle}</h4>
                <ul>
                  {t.compliance.steps.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h4>{t.compliance.mentionsTitle}</h4>
                <ul>
                  {t.compliance.mentions.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h4>{t.compliance.statusesTitle}</h4>
                <ul className="invoice-simple__status-list">
                  {t.compliance.statuses.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <h4>{t.compliance.toolsTitle}</h4>
                <ul>
                  {t.compliance.tools.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="invoice-simple__compliance-warning">{t.labels.complianceWarning}</p>
          </section>
        </div>

        <aside className="invoice-simple__sidebar">
            <div className="invoice-simple__settings-panel">
              <div className="invoice-simple__settings-title">{t.fields.settings}</div>
              <div className="invoice-simple__settings-list">
                <div className="invoice-simple__setting-row">
                  <span>{t.fields.vat}</span>
                  <label className="invoice-simple__toggle-pill">
                    <input name="vatEnabled" type="checkbox" checked={form.vatEnabled} onChange={onChange} />
                    <span className="invoice-simple__toggle-option">Non</span>
                    <span className="invoice-simple__toggle-option">Oui</span>
                  </label>
                </div>
                <div className="invoice-simple__setting-row">
                  <span>{t.fields.vatRate}</span>
                  <input
                    className="invoice-simple__select-like"
                    name="vatRate"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={form.vatRate}
                    onChange={onChange}
                    disabled={!form.vatEnabled}
                  />
                </div>
                <div className="invoice-simple__setting-row">
                  <span>{t.fields.qty}</span>
                  <label className="invoice-simple__toggle-pill">
                    <input name="showQty" type="checkbox" checked={form.showQty} onChange={onChange} />
                    <span className="invoice-simple__toggle-option">Non</span>
                    <span className="invoice-simple__toggle-option">Oui</span>
                  </label>
                </div>
                <div className="invoice-simple__setting-row">
                  <span>{t.fields.unit}</span>
                  <label className="invoice-simple__toggle-pill">
                    <input name="showUnit" type="checkbox" checked={form.showUnit} onChange={onChange} />
                    <span className="invoice-simple__toggle-option">Non</span>
                    <span className="invoice-simple__toggle-option">Oui</span>
                  </label>
                </div>
                <div className="invoice-simple__setting-row">
                  <span>{t.fields.accent}</span>
                  <input
                    className="invoice-simple__select-like invoice-simple__color"
                    name="accent"
                    type="color"
                    value={form.accent}
                    onChange={onChange}
                  />
                </div>
                <div className="invoice-simple__setting-row">
                  <span>{t.fields.currency}</span>
                  <select className="invoice-simple__select-like" name="currency" value={form.currency} onChange={onChange}>
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
                <div className="invoice-simple__setting-row invoice-simple__setting-row--stack">
                  <span>{t.fields.logo}</span>
                  <label className="invoice-simple__upload">
                    <span className="invoice-simple__upload-button">{t.fields.chooseLogo}</span>
                    <span className="invoice-simple__upload-name">{form.logoName || 'PNG, JPG, SVG'}</span>
                    <input type="file" accept="image/*" onChange={onLogoChange} />
                  </label>
                </div>
                <div className="invoice-simple__setting-row">
                  <span>{t.fields.showLegal}</span>
                  <label className="invoice-simple__toggle-pill">
                    <input name="showLegal" type="checkbox" checked={form.showLegal} onChange={onChange} />
                    <span className="invoice-simple__toggle-option">Non</span>
                    <span className="invoice-simple__toggle-option">Oui</span>
                  </label>
                </div>
              </div>
            <div className="invoice-simple__sidebar-actions">
              <button type="button" className="invoice-simple__button invoice-simple__button--download" onClick={() => exportViaApi('/api/generateInvoicePdf', `${invoiceNumber}.pdf`, setIsGeneratingPdf, buildInvoicePdfBytes)} disabled={isGeneratingPdf}>
                {isGeneratingPdf ? '...' : t.actions.pdf}
              </button>
              <button type="button" className="invoice-simple__button secondary" onClick={() => exportViaApi('/api/generateFacturX', `${invoiceNumber}-facturx.pdf`, setIsGeneratingFacturX, buildFacturXPdfBytes)} disabled={isGeneratingFacturX}>
                {isGeneratingFacturX ? '...' : t.actions.facturx}
              </button>
              <button type="button" className="invoice-simple__button primary" onClick={exportXml}>
                {t.actions.send}
              </button>
              <button type="button" className="invoice-simple__button secondary" onClick={exportXml}>
                {t.actions.cii}
              </button>
              <button type="button" className="invoice-simple__button secondary" onClick={exportUbl}>
                {t.actions.ubl}
              </button>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}

export default ServiceSpotlight;
